import React, { useState, useEffect, useRef } from "react";
import "./feePaymentTable.css";
import { Link } from "react-router-dom";
import Filter from "../../Assets/Images/CommanImages/filter.png";
import columnaddition from "../../Assets/Images/CommanImages/rectangleplus.png";
import updown from "../../Assets/Images/CommanImages/updown.png";
import searchicon from "../../Assets/Images/CommanImages/searchyellow.png";
import leftArrow from "../../Assets/Images/CommanImages/Larrowbar.png";
import rightArrow from "../../Assets/Images/CommanImages/Rarrowbar.png";
import SelectedValuesDropdown from "../../Components/Shared/SelectedValuesDropdown/SelectDropdown";
import FileSaver from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const FeePaymentTable = ({
  headers,
  data,
  searchPlaceholder,
  selectedRows,
  setSelectedRows,
  bulk = false,
}) => {
  const [isColumnDivVisible, setIsColumnDivVisible] = useState(false);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const columnDropdownRef = useRef(null);

  const [filters, setFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [isFiltersApplied, setIsFiltersApplied] = useState(false);

  // Student ID must stay out of every user-facing column list (default
  // visible columns, Column Selection, filters) while remaining in the
  // raw `headers`/`data` arrays for internal lookups - the "record
  // payment" link below reads row[0] directly, which is Student ID
  // regardless of display/column-selection visibility.
  const selectableHeaders = headers.filter(
    (header) => header && header !== "Student ID"
  );

  useEffect(() => {
    setVisibleColumns(selectableHeaders.slice(0, 6));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers]);

  const handleColumnSelection = (column) => {
    if (visibleColumns.includes(column)) {
      setVisibleColumns(visibleColumns.filter((col) => col !== column));
    } else {
      setVisibleColumns([...visibleColumns, column]);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleApplyFilter = () => {
    if (Object.keys(filters).length > 0) {
      setAppliedFilters({ ...filters });
      setCurrentPage(1);
      setIsFiltersApplied(true);
      setIsFilterVisible(false);
    }
  };

  const handleResetFilter = () => {
    setFilters({});
    setAppliedFilters({});
    setIsFiltersApplied(false);
  };

  const filteredData = data.filter((row) => {
    const matchesSearchQuery = row.some(
      (cell) =>
        cell && String(cell).toLowerCase().includes(searchQuery.toLowerCase())
    );

    const matchesFilters = Object.entries(appliedFilters).every(
      ([key, value]) => {
        const colIndex = headers.indexOf(key);
        return value ? row[colIndex] === value : true;
      }
    );

    return matchesSearchQuery && matchesFilters;
  });

  const handleSort = (column) => {
    let direction = "asc";
    if (sortConfig.key === column && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: column, direction });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        columnDropdownRef.current &&
        !columnDropdownRef.current.contains(event.target)
      ) {
        setIsColumnDivVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const sortedData = React.useMemo(() => {
    let sortableData = [...filteredData];
    if (sortConfig.key !== null) {
      const colIndex = headers.indexOf(sortConfig.key);
      sortableData.sort((a, b) => {
        if (a[colIndex] < b[colIndex]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[colIndex] > b[colIndex]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [filteredData, sortConfig, headers]);

  const handleExport = (type) => {
    const exportData = sortedData.map((row) =>
      visibleColumns.map((column) => row[headers.indexOf(column)])
    );

    const fileName = prompt("Enter the file name:");
    if (!fileName) return;

    if (type === "excel" || type === "csv") {
      const worksheet = XLSX.utils.aoa_to_sheet([visibleColumns, ...exportData]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

      const fileExtension = type === "csv" ? "csv" : "xlsx";
      const mimeType =
        type === "csv"
          ? "text/csv"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      const file = XLSX.write(workbook, { bookType: fileExtension, type: "array" });
      const blob = new Blob([file], { type: mimeType });
      FileSaver.saveAs(blob, `${fileName}.${fileExtension}`);
    } else if (type === "pdf") {
      const doc = new jsPDF();
      doc.autoTable({ head: [visibleColumns], body: exportData });
      doc.save(`${fileName}.pdf`);
    }
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            style={{ borderRadius: "5px" }}
            className={i === currentPage ? "active" : ""}
          >
            {i}
          </button>
        );
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 3; i++) {
          pages.push(
            <button
              key={i}
              style={{ borderRadius: "5px" }}
              onClick={() => handlePageChange(i)}
              className={i === currentPage ? "active" : ""}
            >
              {i}
            </button>
          );
        }
        pages.push(<span style={{ borderRadius: "5px" }} key="dots1">...</span>);
        pages.push(
          <button key={totalPages} onClick={() => handlePageChange(totalPages)} style={{ borderRadius: "5px" }}>
            {totalPages}
          </button>
        );
      } else if (currentPage > 3 && currentPage < totalPages - 2) {
        pages.push(
          <button key={1} onClick={() => handlePageChange(1)} style={{ borderRadius: "5px" }}>1</button>
        );
        pages.push(<span style={{ borderRadius: "5px" }} key="dots1">...</span>);
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(
            <button
              key={i}
              onClick={() => handlePageChange(i)}
              style={{ borderRadius: "5px" }}
              className={i === currentPage ? "active" : ""}
            >
              {i}
            </button>
          );
        }
        pages.push(<span style={{ borderRadius: "5px" }} key="dots2">...</span>);
        pages.push(
          <button key={totalPages} onClick={() => handlePageChange(totalPages)} style={{ borderRadius: "5px" }}>
            {totalPages}
          </button>
        );
      } else {
        pages.push(
          <button key={1} onClick={() => handlePageChange(1)} style={{ borderRadius: "5px" }}>1</button>
        );
        pages.push(<span style={{ borderRadius: "5px" }} key="dots1">...</span>);
        for (let i = totalPages - 2; i <= totalPages; i++) {
          pages.push(
            <button
              key={i}
              onClick={() => handlePageChange(i)}
              style={{ borderRadius: "5px" }}
              className={i === currentPage ? "active" : ""}
            >
              {i}
            </button>
          );
        }
      }
    }
    return pages;
  };

  const handleSelectAll = (e) => {
    if (!bulk) return;
    if (e.target.checked) {
      setSelectedRows(data.map((row) => row[0]));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (rowIndex) => {
    const actualIndex = (currentPage - 1) * pageSize + rowIndex;
    const rowId = data[actualIndex][0];

    if (bulk) {
      if (selectedRows.includes(rowId)) {
        setSelectedRows(selectedRows.filter((id) => id !== rowId));
      } else {
        setSelectedRows([...selectedRows, rowId]);
      }
    } else {
      setSelectedRows([rowId]);
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "1.5%",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "82vw",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #6A6A6A",
              overflow: "hidden",
            }}
          >
            <input
              type="text"
              placeholder={searchPlaceholder}
              name="search"
              className="search-input-ques-dash"
              onChange={handleSearch}
            />
            <div
              style={{
                width: "1px",
                height: "24px",
                backgroundColor: "#2F2E40",
                margin: "0 4px",
              }}
            ></div>
            <img src={searchicon} alt="search" className="search-icon-ques-dash" />
          </div>
          <div style={{ textAlign: "end", marginTop: "20px", color: "#424242" }}>
            Displaying{" "}
            {Math.min((currentPage - 1) * pageSize + 1, sortedData.length)} -{" "}
            {Math.min(currentPage * pageSize, sortedData.length)} of{" "}
            {sortedData.length} records |
            {["10", "25", "50", "100", "500"].map((size) => (
              <span
                key={size}
                onClick={() => handlePageSizeChange(Number(size))}
                style={{
                  cursor: "pointer",
                  margin: "0 5px",
                  textDecoration: size === pageSize.toString() ? "none" : "underline",
                  color: size === pageSize.toString() ? "black" : "#337AB7",
                }}
              >
                {size}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          backgroundColor: "#D6D6D6",
          marginTop: "9px",
          borderTop: "1px solid grey",
          borderBottom: "1px solid grey",
        }}
      >
        <div
          style={{
            backgroundColor: "#FFBD20",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            paddingLeft: "8px",
            width: "90px",
          }}
          onClick={() => setIsFilterVisible(!isFilterVisible)}
        >
          <img src={Filter} style={{ height: "24px", width: "24px" }} alt="filter" />
          <span style={{ marginLeft: "10px", color: "#2F2E40" }}>Filter</span>
        </div>
        <div
          className="col-tc"
          style={{
            marginLeft: "auto",
            textAlign: "end",
            marginRight: "8px",
            display: "flex",
            alignItems: "center",
            position: "relative",
            cursor: "pointer",
            height: "40px",
          }}
          onClick={() => setIsColumnDivVisible(!isColumnDivVisible)}
        >
          <img src={columnaddition} style={{ height: "34px", width: "32px" }} alt="column addition" />
          <span style={{ marginLeft: "4px", color: "#2F2E40", marginRight: "14px", fontSize: "14px" }}>
            Column Selection
          </span>
          {isColumnDivVisible && (
            <div ref={columnDropdownRef}>
              <div className="arrow-up-tc"></div>
              <div className="popupColumn-tc">
                {selectableHeaders.map((column, index) => (
                  <div
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleColumnSelection(column);
                    }}
                  >
                    <input
                      type="checkbox"
                      id={`column-${index}`}
                      name="columnSelection"
                      value={column}
                      className="square-checkbox-tc"
                      checked={visibleColumns.includes(column)}
                      readOnly
                    />
                    <label htmlFor={`column-${index}`}>{column}</label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {data.length > 0 ? (
        <>
          <div
            className="table-container-scrollable"
            style={{
              width: "83vw",
              overflowX: "auto",
              overflowY: "visible",
              WebkitOverflowScrolling: "touch",
              boxSizing: "border-box",
            }}
          >
            <table className="fixed-header-table" style={{ width: "100%", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  {bulk && (
                    <td className="Heading-tc" style={{ width: "40px", paddingLeft: "8px" }}>
                      <input
                        type="checkbox"
                        className="delete-question-checkbox-dqc"
                        checked={selectedRows.length > 0 && selectedRows.length === data.length}
                        onChange={handleSelectAll}
                      />
                    </td>
                  )}
                  {visibleColumns.map((heading, index) => (
                    <td
                      key={index}
                      className="Heading-tc fpd-heading-cell"
                      style={{
                        textAlign: "left",
                        cursor: "pointer",
                        width: "auto",
                        position: "relative",
                        paddingLeft: "0px",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "left",
                          color: "rgb(47, 46, 64)",
                          fontSize: "14px",
                        }}
                      >
                        {heading}
                        <img
                          onClick={() => handleSort(heading)}
                          src={updown}
                          style={{ height: "20px", width: "20px", marginLeft: "5px" }}
                          alt="sort"
                        />
                      </span>
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, rowIndex) => (
                  <tr key={rowIndex} className={rowIndex % 2 === 1 ? "even-row-tc" : ""}>
                    {bulk && (
                      <td style={{ width: "40px", paddingLeft: "8px" }}>
                        <input
                          type="checkbox"
                          className="delete-question-checkbox-dqc"
                          checked={selectedRows.includes(row[0])}
                          onChange={() => handleRowSelect(rowIndex)}
                        />
                      </td>
                    )}
                    {visibleColumns.map((column, cellIndex) => (
                      <td
                        key={cellIndex}
                        className="fpd-cell"
                        style={{
                          width: "auto",
                          textAlign: "left",
                          color: "#6A6A6A",
                          verticalAlign: "middle",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "200px",
                        }}
                      >
                        {!bulk && cellIndex === 0 ? (
                          <Link
                            style={{ color: "#337ab7" }}
                            to={`/record_payment?studentId=${row[0]}&from=fee_payment_dashboard`}
                          >
                            <span className="truncated-text" title={row[headers.indexOf(column)]}>
                              {row[headers.indexOf(column)]}
                            </span>
                          </Link>
                        ) : (
                          <span className="truncated-text-2">
                            {row[headers.indexOf(column)]}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination-tc">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="extreme-left-tc-pagination"
            >
              <img src={leftArrow} alt="" />
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="lefttc-pagination"
            >
              {"<"}
            </button>
            {renderPagination()}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="righttc-pagination"
            >
              {">"}
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="extreme-right-tc-pagination"
            >
              <img src={rightArrow} alt="" />
            </button>
          </div>
        </>
      ) : (
        "No data available"
      )}

      {isFilterVisible && (
        <div className="filter-sidebar-tc">
          <div className="filter-content-tc" style={{ position: "relative" }}>
            <button
              className="repman-cross-btn-HOD"
              onClick={() => setIsFilterVisible(false)}
            >
              <p>X</p>
            </button>
            <h3 className="AdvanceF-heading-tc">Filter</h3>
            <div className="filter-apply-reset-ques-rev-dash">
              <button className="create-test-section-btn" onClick={handleApplyFilter}>
                APPLY
              </button>
              {isFiltersApplied && (
                <button className="create-test-section-btn" onClick={handleResetFilter}>
                  RESET
                </button>
              )}
            </div>
            <div className="filter-options-container">
              {selectableHeaders.map((header) => {
                const originalIndex = headers.indexOf(header);
                let uniqueOptions = [
                  ...new Set(
                    data.map((row) => row[originalIndex]).filter((option) => option)
                  ),
                ];
                uniqueOptions.sort((a, b) => (a > b ? 1 : -1));
                return (
                  <SelectedValuesDropdown
                    key={originalIndex}
                    label={header}
                    width="300px"
                    marginLeft="15px"
                    marginTop="18px"
                    setSelected={(selectedOption) => {
                      setFilters((prevFilters) => ({
                        ...prevFilters,
                        [header]: selectedOption,
                      }));
                    }}
                    options={uniqueOptions.map((value) => ({ label: value, id: value }))}
                    selected={filters[header] || ""}
                    Height="auto"
                    OverFlow="auto"
                    minheight="200px"
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeePaymentTable;